import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { auth, db } from '../firebase';
import Footer from '../components/Footer';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  setDoc,
  serverTimestamp // ← ✅ Add this
} from 'firebase/firestore';
import { Link, useLocation } from 'react-router-dom';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { useNotifications } from '../contexts/NotificationContext';
import UserGroupCard from '../components/UserGroupCard';
import { unreadCommentCount } from '../utils/matchThread';
import { getMembershipBadge } from '../utils/getMembershipBadge';
import { groupMatchesByUser } from '../utils/groupMatchesByUser';
import { useMatchFilters } from '../hooks/useMatchFilters';
import MatchFilterBar from '../components/MatchFilterBar';
import MatchesMobileChromeHeader, { MATCH_VIEW_TABS } from '../components/MatchesMobileChromeHeader';
import { useRouteSwipeViews } from '../hooks/useSwipeViews';
import { useRegisterPullToRefresh } from '../hooks/useRegisterPullToRefresh';

// Session-storage key for the matches scroll/filter snapshot. Used so a
// click → "View Match Details" → "Back to Matches" round-trip lands the
// user at the same scroll position and with the same filters they had
// before. Mirrors the pattern used by SearchResults / WantListMatches /
// LatestPedals. NOT used to persist the matches data itself — that comes
// from a live `onSnapshot` listener and will repopulate on remount.
const MATCHES_STORAGE_KEY = 'matches_scrollState';

// Function to calculate distance between two coordinates using Haversine formula
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Function to get coordinates from user's shipping address using Google Maps API
const getUserCoordinates = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return null;
    
    const userData = userDoc.data();
    const shippingAddress = userData.shippingAddress;
    
    if (!shippingAddress || !shippingAddress.city || !shippingAddress.state) {
      console.log(`📍 No shipping address found for user ${userId}`);
      return null;
    }
    
    // Create address string from user's shipping address
    const addressString = `${shippingAddress.street1}, ${shippingAddress.city}, ${shippingAddress.state} ${shippingAddress.zip}`;
    
    // Use Google Maps Geocoding API
    const results = await geocodeByAddress(addressString);
    if (results && results[0]) {
      const { lat, lng } = await getLatLng(results[0]);
      console.log(`📍 User ${userId} coordinates: ${lat}, ${lng} from address: ${addressString}`);
      return { lat, lng };
    }
    
    console.log(`📍 Could not geocode address for user ${userId}: ${addressString}`);
    return null;
  } catch (error) {
    console.error(`❌ Error getting coordinates for user ${userId}:`, error);
    return null;
  }
};


/**
 * Group flat enhanced-match array into counterparty user groups, sub-grouped
 * by "the single pedal in a constellation." Pure function — input is the
 * already-perspective-swapped, already-enhanced match array; output is the
 * render-ready group structure.
 *
 * See src/utils/groupMatchesByUser.js for the implementation.
 */

const Matches = () => {
  const { markMatchNotificationsAsRead } = useNotifications();
  const location = useLocation();

  // Native-only swipe + slide across the Matches views (All / Saved / Hidden).
  const { swipeProps, viewClass } = useRouteSwipeViews(MATCH_VIEW_TABS, location.pathname, { liveDrag: true });

  // Mark match notifications as read ONLY on initial mount (when user navigates to this page)
  // Don't re-run this effect when new notifications arrive while already on this page
  useEffect(() => {
    // includeBatch: this is the page a batched "N new matches" alert points at.
    markMatchNotificationsAsRead({ includeBatch: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array = only runs on mount

  // Helper functions for membership badges
  const getMembershipProfileBadge = (membershipTier) => {
    switch (membershipTier) {
      case 'premium_basic':
        return (
          <div style={{
            position: 'absolute',
            top: '-3px',
            left: '-3px',
            backgroundColor: '#3B82F6',
            borderRadius: '8px',
            minWidth: '24px',
            height: '16px',
            padding: '0 4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #fff',
            fontSize: '9px',
            fontWeight: 'bold',
            color: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
          }}>
            9V
          </div>
        );
      case 'premium_plus':
        return (
          <div style={{
            position: 'absolute',
            top: '-3px',
            left: '-3px',
            backgroundColor: '#9333EA',
            borderRadius: '8px',
            minWidth: '26px',
            height: '16px',
            padding: '0 4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #fff',
            fontSize: '9px',
            fontWeight: 'bold',
            color: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
          }}>
            18V
          </div>
        );
      default:
        return null;
    }
  };
  
  const [matches, setMatches] = useState([]);
  // Unread match comments keyed by match id (the sorted listing pair): the
  // other member's messages newer than my read marker, which only the match
  // page writes once its messages have loaded. Own comments never count
  // (owner correction, 2026-11-01).
  const [threadsByKey, setThreadsByKey] = useState({});
  const [matchesLoading, setMatchesLoading] = useState(true);
  const [savedMatchIds, setSavedMatchIds] = useState([]);
  const [hiddenMatchIds, setHiddenMatchIds] = useState([]);
  // New counterparty-keyed save/hide fields. Coexist with the old per-matchId
  // arrays during the PR1→PR2 gap. After PR 2's migration, the old arrays
  // become dormant and these are the source of truth.
  const [savedMatchUsers, setSavedMatchUsers] = useState([]);
  const [hiddenMatchUsers, setHiddenMatchUsers] = useState([]);
  const [matchesRefreshNonce, setMatchesRefreshNonce] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Extracted filter system — owns filter state, available-options derivation,
  // apply-filters pipeline, bottom-sheet open/close, activeFilterCount,
  // sessionStorage seed, etc. See src/hooks/useMatchFilters.js.
  const filters = useMatchFilters({
    matches,
    hiddenMatchIds,
    storageKey: MATCHES_STORAGE_KEY
  });
  const {
    filteredMatches,
    savedState: savedMatchesState,
    getFilterStateSnapshot,
  } = filters;
// Cross-tab auth safety refs. See the auth effect below for full context —
// these prevent a "logged out" flicker on the matches grid when the user
// right-clicks → opens a link in a new tab. Mirrors RecommendationsPage.
const authSettledRef = useRef(false);
const lastFetchedUidRef = useRef(null);
// Tracks whether we've already restored the scroll position for this
// mount. We only want to restore ONCE — subsequent filter changes or
// real-time match updates should NOT re-fire the scroll.
const hasRestoredRef = useRef(false);
const [showBackToTop, setShowBackToTop] = useState(false);

useEffect(() => {
  const handleScroll = () => {
    setShowBackToTop(window.scrollY > 300);
  };
  window.addEventListener('scroll', handleScroll, { passive: true });
  return () => window.removeEventListener('scroll', handleScroll);
}, []);

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// Snapshot the current filter state + scroll position to sessionStorage.
// Called from the "View Match Details" Link onClick (so the standard
// back-to-matches round-trip restores cleanly) and from the beforeunload
// listener below (so refreshes / cross-tab Firebase reinits also restore).
// We deliberately do NOT serialize the matches data itself — it comes
// from a live `onSnapshot` listener and will repopulate on remount.
const saveScrollState = () => {
  const state = {
    ...getFilterStateSnapshot(),
    scrollY: window.scrollY,
    timestamp: Date.now()
  };
  sessionStorage.setItem(MATCHES_STORAGE_KEY, JSON.stringify(state));
};

// Beforeunload safety-net: covers refreshes, unexpected reloads, and the
// cross-tab Firebase reinit case (where this tab momentarily reloads
// because of an auth-state ping from a sibling tab). Mirrors LatestPedals.
useEffect(() => {
  const handleBeforeUnload = () => {
    if (filteredMatches.length > 0) {
      saveScrollState();
    }
  };
  window.addEventListener('beforeunload', handleBeforeUnload);
  return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [getFilterStateSnapshot, filteredMatches]);

// Restore scroll position once the matches grid has rendered.
//
// Why not restore synchronously on mount? Because matches are loaded via
// live `onSnapshot` listeners (see the data effect below) — at mount
// time the matches array is empty and the page is still skeleton-loading.
// We wait for `matchesLoading` to settle AND for at least one filtered
// match to exist (the filter pipeline runs as a side-effect of the
// matches arriving), then scroll once. The `hasRestoredRef` guard
// prevents subsequent filter tweaks or real-time match updates from
// re-firing the scroll.
useEffect(() => {
  if (hasRestoredRef.current) return;
  if (!savedMatchesState) return;
  if (matchesLoading) return;
  if (filteredMatches.length === 0) return;
  hasRestoredRef.current = true;
  // Defer one frame so the grid has finished laying out before we scroll.
  setTimeout(() => {
    window.scrollTo(0, savedMatchesState.scrollY || 0);
  }, 50);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [matchesLoading, filteredMatches]);

// Clear the saved snapshot when the user navigates somewhere that ISN'T
// /matches or /match/:id. Without this, returning to /matches days later
// (via the dashboard, search, etc.) could try to restore stale scroll
// against a totally different matches grid. The cleanup function runs
// on each location change AND on unmount; checking `window.location` at
// cleanup time gives us the destination route.
useEffect(() => {
  return () => {
    const next = window.location.pathname;
    const stayingInMatchesFlow = next === '/matches' || next.startsWith('/match/');
    if (!stayingInMatchesFlow) {
      sessionStorage.removeItem(MATCHES_STORAGE_KEY);
    }
  };
}, [location]);

// Per-match save/unsave/hide handlers kept for reference and to keep the old
// SavedMatches.jsx / HiddenMatches.jsx pages aligned during the PR1→PR2 gap.
// The main Matches page no longer wires these to any buttons after Section 2's
// JSX swap — group-level handlers (handleGroupSave / handleGroupHide below)
// replace them on this surface. Will be removed in PR 2 once the secondary
// pages migrate to the counterparty-keyed model.
// eslint-disable-next-line no-unused-vars
const saveMatchToFirestore = async (match) => {
  const user = auth.currentUser;
  if (!user) return;

  const matchId = `${match.myPedal.id}_${match.matchedPedal.id}`;
  const userRef = doc(db, 'users', user.uid);

  try {
    await updateDoc(userRef, {
      savedMatches: arrayUnion(matchId)
    });

    console.log('Match saved to savedMatches array.');
  } catch (error) {
    console.error('Error updating savedMatches array:', error);
  }
};

// eslint-disable-next-line no-unused-vars
const unsaveMatchFromFirestore = async (match) => {
  const user = auth.currentUser;
  if (!user) return;

  const matchId = `${match.myPedal.id}_${match.matchedPedal.id}`;
  const userRef = doc(db, 'users', user.uid);

  try {
    await updateDoc(userRef, {
      savedMatches: arrayRemove(matchId)
    });

    console.log('Match removed from savedMatches array.');
  } catch (error) {
    console.error('Error removing from savedMatches array:', error);
  }
};

// eslint-disable-next-line no-unused-vars
const hideMatchFromFirestore = async (match) => {
  const user = auth.currentUser;
  if (!user) return;

  const matchId = `${match.myPedal.id}_${match.matchedPedal.id}`;
  const userRef = doc(db, 'users', user.uid);

  try {
    await updateDoc(userRef, {
      hiddenMatches: arrayUnion(matchId)
    });

    console.log('Match hidden.');
  } catch (error) {
    console.error('Error hiding match:', error);
  }
};

// ── New: group-level save / hide handlers ─────────────────────────────────
// Saved is keyed by counterparty UID, NOT by matchId, so future matches with
// the same counterparty automatically appear inside the saved card on the
// (PR-2-rewritten) SavedMatches page. Old per-matchId `savedMatches` array is
// intentionally NOT touched — PR 2's migration consolidates.
const handleGroupSave = async (group, currentlySaved) => {
  const user = auth.currentUser;
  if (!user) return;
  const userRef = doc(db, 'users', user.uid);
  try {
    await updateDoc(userRef, {
      savedMatchUsers: currentlySaved
        ? arrayRemove(group.counterpartyUid)
        : arrayUnion(group.counterpartyUid),
      savedMatches: currentlySaved
        ? arrayRemove(...group.allMatchIds)
        : arrayUnion(...group.allMatchIds)
    });
  } catch (err) {
    console.error('Group save toggle failed:', err);
  }
};

// Hide snapshots the current matchIds with that counterparty AND removes the
// saved state — keeps Hide-on-Saved and Hide-on-Matches behaviorally
// identical. The auto-unhide effect re-emerges the card on Matches when a
// new matchId (not in the snapshot) appears, but it will NOT re-appear on
// /saved-matches because the saved state was cleared at hide time. Per
// product direction: "if someone clicks hide, it should only appear in the
// hidden matches." Full-array replacement is used on hiddenMatchUsers (rather
// than arrayUnion with an object) to avoid Firestore object-equality
// subtleties. See chat 2026-05-30.
const handleGroupHide = async (group, currentHiddenMatchUsers) => {
  const user = auth.currentUser;
  if (!user) return;
  const alreadyHidden = currentHiddenMatchUsers.some(
    h => h.counterpartyUid === group.counterpartyUid
  );
  const newEntry = {
    counterpartyUid: group.counterpartyUid,
    hiddenMatchIds: [...group.allMatchIds].sort()
  };
  const userRef = doc(db, 'users', user.uid);
  try {
    await updateDoc(userRef, {
      hiddenMatchUsers: alreadyHidden
        ? currentHiddenMatchUsers
        : [...currentHiddenMatchUsers, newEntry],
      hiddenMatches: arrayUnion(...group.allMatchIds),
      savedMatchUsers: arrayRemove(group.counterpartyUid),
      savedMatches: arrayRemove(...group.allMatchIds)
    });
  } catch (err) {
    console.error('Group hide failed:', err);
  }
};



  useEffect(() => {
    // Cross-tab safe auth listener.
    //
    // Firebase Auth syncs session state across tabs of the same origin.
    // When the user right-clicks a "View Match Details" link and chooses
    // "Open in new tab", the newly opened tab boots Firebase and can
    // briefly emit a `null` user to *this* (originating) tab before the
    // session re-hydrates. The naive handler (just `setCurrentUser(user);
    // setAuthLoading(false);`) would then render the "Create an account
    // to start trading!" gate at line ~795 and tear down the live matches
    // listeners below, only to re-mount them a tick later. The pattern
    // below treats the FIRST null emission as "auth still settling" and
    // only honors a `null` once we've previously seen a real user.
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        authSettledRef.current = true;
        setCurrentUser(user);
        setAuthLoading(false);
      } else if (authSettledRef.current) {
        // Auth has settled before — treat null as a real logout.
        setCurrentUser(null);
        setAuthLoading(false);
      } else {
        // First emission was null — auth still initializing; wait.
        setAuthLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const refreshMatches = useCallback(async () => {
    lastFetchedUidRef.current = null;
    setMatchesRefreshNonce((n) => n + 1);
  }, []);

  useRegisterPullToRefresh(refreshMatches);

  useEffect(() => {
    const userId = currentUser?.uid;
    if (!userId) {
      setMatchesLoading(false);
      // Reset so that when the user signs back in (real logout → login)
      // we DO re-subscribe to their fresh matches.
      lastFetchedUidRef.current = null;
      return;
    }
    // Skip redundant re-subscriptions for the same uid. Without this, any
    // re-emission of the same uid from `onAuthStateChanged` (e.g. token
    // refresh, cross-tab sync ticks) would tear down the live onSnapshot
    // listeners below and rebuild them — wasteful and briefly visible to
    // the user as a re-loading state.
    if (userId === lastFetchedUidRef.current) return;
    lastFetchedUidRef.current = userId;

    // Reset loading state when effect re-runs (e.g., on auth state change)
    setMatchesLoading(true);

    // Unread match comments, LIVE: one listener over every thread this member
    // is in (the matchThreads list rule is "uid in participants", which the
    // array-contains filter proves). A comment arriving while the member is
    // already on this page updates the badge in place; the earlier one-shot
    // read only refreshed on reload (owner, 2026-11-01). Best effort: an
    // error here only hides the badges.
    const unsubThreads = onSnapshot(
      query(collection(db, 'matchThreads'), where('participants', 'array-contains', userId)),
      (threadSnap) => {
        const byKey = {};
        threadSnap.forEach((d) => {
          const unread = unreadCommentCount(d.data(), userId);
          if (unread > 0) byKey[d.id] = unread;
        });
        setThreadsByKey(byKey);
      },
      (threadErr) => console.warn('Match comment indicators unavailable:', threadErr?.message || threadErr)
    );

    const userRef = doc(db, 'users', userId);
    const unsubscribeSaved = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setSavedMatchIds(data.savedMatches || []);
        setHiddenMatchIds(data.hiddenMatches || []);
        setSavedMatchUsers(data.savedMatchUsers || []);
        setHiddenMatchUsers(data.hiddenMatchUsers || []);
      }
    });

    // Query matches collection for BOTH sides (where I'm myPedalUser OR matchedPedalUser)
    console.log('📡 Firestore query: matches for user', userId);
    const myMatchesQuery = query(
      collection(db, 'matches'),
      where('myPedalUser', '==', userId)
    );
    const theirMatchesQuery = query(
      collection(db, 'matches'),
      where('matchedPedalUser', '==', userId)
    );

    let myMatches = [];
    let theirMatches = [];
    
    const processMatches = async () => {
      console.log('📥 My matches:', myMatches.length, '| Their matches:', theirMatches.length);
      
        // Combine both sets of matches
      const myMatchDocs = myMatches.map(doc => ({ id: doc.id, ...doc.data() }));
      const theirMatchDocs = theirMatches.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        // Swap the pedals and users so the current user is always "myPedal"
        myPedal: doc.data().matchedPedal,
        matchedPedal: doc.data().myPedal,
        myPedalUser: doc.data().matchedPedalUser,
        matchedPedalUser: doc.data().myPedalUser
      }));
      
      const allMatches = [...myMatchDocs, ...theirMatchDocs];
      
      // Enhance matches with user info
        const getTradeBadge = (count) => {
          if (count === undefined || count === null || isNaN(count)) return 0;
          return count;
        };

      const enhancedMatches = await Promise.all(
        allMatches.map(async (matchDoc) => {
          // PII isolation: counterparty cards only need username/photo/badges/tier — read from public mirror.
          const myUserRef = doc(db, 'publicProfiles', matchDoc.myPedalUser);
          const theirUserRef = doc(db, 'publicProfiles', matchDoc.matchedPedalUser);

            const [myUserSnap, theirUserSnap] = await Promise.all([
              getDoc(myUserRef),
              getDoc(theirUserRef)
            ]);

            const myData = myUserSnap.exists() ? myUserSnap.data() : {};
            const theirData = theirUserSnap.exists() ? theirUserSnap.data() : {};

            return {
            // matchId and counterpartyUid are preserved on the enhanced match so
            // that the new user-grouped card renderer (groupMatchesByUser below)
            // can key on the counterparty without re-deriving from the
            // overloaded matchedPedalUser slot, which gets overwritten with a
            // profile object on the line below. matchId is the Firestore doc id
            // (already in sorted-pedal-id form, see matchMaker.js).
            matchId: matchDoc.id,
            counterpartyUid: matchDoc.matchedPedalUser,
            myPedal: matchDoc.myPedal,
            matchedPedal: matchDoc.matchedPedal,
            createdAt: matchDoc.createdAt,
              myPedalUser: {
              username: myData.username || 'Unknown',
              badge: getTradeBadge(myData.completedTrades || 0),
              membershipTier: myData.membershipTier || 'free',
              photoURL: myData.photoURL || null
              },
              matchedPedalUser: {
              username: theirData.username || 'Unknown',
              badge: getTradeBadge(theirData.completedTrades || 0),
              membershipTier: theirData.membershipTier || 'free',
              photoURL: theirData.photoURL || null
              }
            };
          })
        );

      // Per-match sort (18V-first, newest-first) used to live here. Removed
      // because the page now renders user-grouped cards — sort happens at the
      // group level inside groupMatchesByUser() instead, preserving the same
      // promoted-counterparty-first / newest-activity-first ordering.
      console.log('✅ Matches processed:', enhancedMatches.length);
setMatches(enhancedMatches);
setMatchesLoading(false);
    };

    const unsubMyMatches = onSnapshot(myMatchesQuery, (mySnap) => {
      myMatches = mySnap.docs;
      processMatches();
    });

    const unsubTheirMatches = onSnapshot(theirMatchesQuery, (theirSnap) => {
      theirMatches = theirSnap.docs;
      processMatches();
    });

    return () => {
      unsubscribeSaved();
      unsubMyMatches();
      unsubTheirMatches();
      unsubThreads();
    };
  }, [currentUser, matchesRefreshNonce]);

  // Filter state, available-options derivation, and apply-filters pipeline
  // all live in useMatchFilters (see the hook call near the top of this
  // component). The hook publishes `filteredMatches` which the group-card
  // derivations below consume via `passingMatchIds`.

  const getMatchId = (match) => {
    return `${match.myPedal?.id}_${match.matchedPedal?.id}`;
  };

  // ── Group-card derivations ────────────────────────────────────────────
  // Set of matchIds that pass the active content filters. Used only as the
  // "filter gate" for which user-groups appear on the page. Group CONTENT
  // (the pedals inside a card) always shows the full visible set for that
  // counterparty — interpretation A from product direction.
  const passingMatchIds = useMemo(
    () => new Set(filteredMatches.map(m => m.matchId)),
    [filteredMatches]
  );

  // Visible match set used as the source for group content: hidden-filter
  // applied (both old per-matchId hides AND new counterparty hides), content
  // filter NOT applied. This produces interpretation A: if any of a
  // counterparty's matches passes the filter, the whole card shows with all
  // visible matches.
  const visibleMatches = useMemo(() => {
    if (!matches || matches.length === 0) return [];
    return matches.filter(match => {
      if (hiddenMatchIds.includes(match.matchId)) return false;
      const hiddenEntry = hiddenMatchUsers.find(h => h.counterpartyUid === match.counterpartyUid);
      if (hiddenEntry && hiddenEntry.hiddenMatchIds.includes(match.matchId)) return false;
      return true;
    });
  }, [matches, hiddenMatchIds, hiddenMatchUsers]);

  // The grouped cards to render.
  const userGroups = useMemo(
    () => groupMatchesByUser(visibleMatches, passingMatchIds),
    [visibleMatches, passingMatchIds]
  );

  // Auto-unhide: when a NEW matchId arrives for a counterparty in
  // hiddenMatchUsers (not in the original snapshot), drop the entry entirely
  // and the card reappears on the main page. Per product direction: "the
  // hide was about the existing noise, not about that user forever."
  //
  // Uses full-array replacement rather than arrayRemove-with-object because
  // Firestore object equality on arrayRemove is subtle. See chat 2026-05-30.
  useEffect(() => {
    if (!currentUser?.uid) return;
    if (hiddenMatchUsers.length === 0) return;
    if (matches.length === 0) return;

    const entriesToUnhide = [];
    const remainingHidden = hiddenMatchUsers.filter(entry => {
      const currentMatchIdsForUid = matches
        .filter(m => m.counterpartyUid === entry.counterpartyUid)
        .map(m => m.matchId);
      const hasNewMatch = currentMatchIdsForUid.some(id => !entry.hiddenMatchIds.includes(id));
      if (hasNewMatch) entriesToUnhide.push(entry);
      return !hasNewMatch;
    });

    if (remainingHidden.length === hiddenMatchUsers.length) return;

    const userRef = doc(db, 'users', currentUser.uid);
    updateDoc(userRef, {
      hiddenMatchUsers: remainingHidden,
      hiddenMatches: arrayRemove(...entriesToUnhide.flatMap(e => e.hiddenMatchIds))
    }).catch(err => {
      console.error('Auto-unhide failed:', err);
    });
  }, [matches, hiddenMatchUsers, currentUser]);

  return (
    <>
      <style>{`
        @media (min-width: 769px) {
          .matches-box {
            padding-top: 0.75rem !important;
            padding-bottom: 0.75rem !important;
            padding-left: 3px !important;
            padding-right: 3px !important;
          }
          .matches-main-container {
            max-width: 1100px !important;
            padding: 0 2rem !important;
            box-sizing: border-box !important;
          }
        }
        @media (max-width: 768px) {
          .matches-main-container {
            display: block !important;
            width: auto !important;
            margin: 0 0.5rem 0 0.5rem !important;
            max-width: none !important;
            min-width: auto !important;
            padding: 0 !important;
          }
          .match-card {
            width: calc(100vw - 2rem) !important;
            max-width: 100% !important;
            padding: 8px !important;
          }
          .large-match-image {
            width: 92% !important;
          }
          .match-pedal-make {
            font-size: 1.125rem !important;
          }
          .match-pedal-model {
            font-size: 1.125rem !important;
          }
          .match-pedal-details {
            font-size: 0.925rem !important;
          }
          .match-overlay-text {
            padding: 7px 11px !important;
            font-size: 13.5px !important;
            top: 10px !important;
            left: 10px !important;
            border-radius: 7px !important;
          }
          .matches-box {
            padding: 1rem !important;
          }
        }
      `}</style>
      <div {...swipeProps} className="min-h-screen" style={{ backgroundColor: 'transparent', minHeight: 'auto', display: 'flex', flexDirection: 'column' }}>
      <div style={{ backgroundColor: '#F7F3EB', flex: 1 }}>
      <div className="matches-page-content" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        <MatchesMobileChromeHeader activePath={location.pathname} filters={filters}>
            <Link
              to="/trading"
              className="matches-header-back-link"
              style={{
                color: '#5A9ECB',
                textDecoration: 'none',
                alignItems: 'center',
                marginTop: '2rem',
                marginBottom: '1rem',
                fontSize: '1.125rem'
              }}
            >
              ← Back to Trading
            </Link>
            <div className="matches-header-expanded-inner" style={{ marginBottom: '2rem' }}>
              <div className="matches-header-copy">
                <h1 className="font-bold text-gray-900" style={{ fontSize: '2.25rem', fontWeight: '700', letterSpacing: '-0.025em' }}>
                  Your Matches
                </h1>
                <p className="matches-header-description text-gray-600" style={{ fontSize: '17px' }}>
                  Possible trades where your Wantlist and another member's Pedalboard overlap
                </p>
              </div>
              {currentUser && (
                <div className="matches-header-links">
                  <Link
                    to="/saved-matches"
                    className="hover:underline"
                    style={{ color: '#5A9ECB', fontWeight: 'bold', fontSize: 'calc(0.875rem + 2pt)', textDecoration: 'none', whiteSpace: 'nowrap' }}
                  >
                    View Saved Matches
                  </Link>
                  <Link
                    to="/hidden-matches"
                    className="hover:underline"
                    style={{ color: '#5A9ECB', fontWeight: 'bold', fontSize: 'calc(0.875rem + 2pt)', textDecoration: 'none', whiteSpace: 'nowrap' }}
                  >
                    View Hidden Matches
                  </Link>
                </div>
              )}
            </div>

            <div className="matches-desktop-filter-bar">
              <MatchFilterBar filters={filters} />
            </div>
        </MatchesMobileChromeHeader>

        <style jsx>{`
          @media (min-width: 769px) {
            .back-to-top-btn {
              right: 160px !important;
            }
          }
        `}</style>

      <div className="swipe-clip">
      <div className={['swipe-pane', viewClass].filter(Boolean).join(' ')}>
      <div className="matches-main-container" style={{ maxWidth: '1100px', width: '100%', margin: '2rem auto 0 auto', padding: '0 2rem', boxSizing: 'border-box' }}>
        {authLoading || matchesLoading ? (
          <div className="text-center text-gray-500 italic mt-8" style={{ width: '100%' }}>
            Loading matches...
          </div>
        ) : !currentUser ? (
          <div style={{ gridColumn: '1 / -1', margin: '2rem auto', maxWidth: '600px' }}>
            <div className='mb-4 p-4 border border-blue-300 rounded-lg bg-blue-50 text-center'>
              <p className='text-blue-800 text-lg font-semibold mb-2'>Create an account to start trading!</p>
              <p className='text-blue-700 text-sm mb-3'>
                Join thousands of musicians trading pedals safely and affordably.
              </p>
              <Link to='/signup' className='inline-block px-4 py-2 text-sm font-medium' style={{ marginRight: '12px', color: '#5A9ECB', textDecoration: 'none' }}>
                Sign Up
              </Link>
              <span className='text-blue-700 text-sm font-medium' style={{ margin: '0 8px' }}>or</span>
              <Link to='/login' className='inline-block px-4 py-2 text-sm font-medium' style={{ marginLeft: '12px', color: '#5A9ECB', textDecoration: 'none' }}>
                Log In
              </Link>
            </div>
          </div>
        ) : userGroups.length === 0 ? (
          <div className='text-center text-gray-500 italic mt-8' style={{ width: '100%' }}>
            {matches.length === 0
              ? "No trade matches yet. They turn up when a pedal on your Wantlist is listed by someone who wants one of yours."
              : filters.activeFilterCount > 0
                ? 'No matches found with this criteria. Try adjusting your filters.'
                : "You don't have any visible matches right now."}
          </div>
        ) : (
          userGroups.map((group) => {
            const isSaved = savedMatchUsers.includes(group.counterpartyUid);
            return (
              <UserGroupCard
                key={group.counterpartyUid}
                group={group}
                isSaved={isSaved}
                threadsByKey={threadsByKey}
                primaryButton={{
                  label: isSaved ? 'Saved' : 'Save Match',
                  onClick: () => handleGroupSave(group, isSaved),
                  outline: isSaved,
                }}
                secondaryButton={{
                  label: 'Hide Match',
                  onClick: () => handleGroupHide(group, hiddenMatchUsers)
                }}
                onRowClick={saveScrollState}
              />
            );
          })
        )}
      </div>

      {/* Fixed gap below content */}
      <div style={{ height: '200px' }}></div>
      </div>
      </div>
      </div>
      </div>
      
      {/* Footer */}
      <Footer />

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="back-to-top-btn"
          style={{
            position: 'fixed',
            bottom: window.innerWidth <= 768 ? '90px' : '30px',
            right: '30px',
            backgroundColor: '#5A9ECB',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#4A8AB0';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#5A9ECB';
            e.target.style.transform = 'scale(1)';
          }}
          aria-label="Back to top"
        >
          ⌃
        </button>
      )}
      </div>
    </>
  );
};

export default Matches;
